import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import {
  DESIGNATION_OPTIONS,
  type FormSubmission,
  type PractitionerDesignation,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

// --- Form PAR 101 (Virginia Power of Attorney) editable payload -------------
// snake_case keys mirror `apps/forms/mappings/form_par101.py` `build_defaults`
// exactly (the `data` blob is passed through verbatim, no snake→camel mapping).

export type Par101GenerateFor = "taxpayer" | "spouse" | "jointly";

/** A representative on the PAR 101 (contact details are looked up from the
 *  practitioner record server-side; only these flags/selection live in `data`). */
export interface Par101Representative {
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
  /** VA enrolled-agent number (printed with the "A-" prefix stripped). */
  agent_no: string;
  /** "Do NOT mail copies of any correspondence to this agent." */
  dont_mail: boolean;
  /** "Mail copies of email communications to this agent." */
  mail_email: boolean;
}

/** A Virginia matter row: rows 1-2 use (tax_type, years); rows 3-6 add a VA
 *  account number and a beginning/ending period. One shape carries both. */
export interface Par101Matter {
  tax_type: string;
  years: string;
  account_no: string;
  period_begin: string;
  period_end: string;
}

export interface Form_par101_Data {
  generate_for: Par101GenerateFor;
  representatives: Par101Representative[];
  matters: Par101Matter[];
  maintain_auth: boolean;
  maintain_for: string;
  revoke_auth: boolean;
  revoke_for: string;
  signer: string;
  signer_title: string;
}

/** The Virginia template holds six matter rows. */
export const MAX_PAR101_MATTERS = 6;

const GENERATE_FOR_OPTIONS: { value: Par101GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EMPTY_MATTER: Par101Matter = {
  tax_type: "",
  years: "",
  account_no: "",
  period_begin: "",
  period_end: "",
};

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  maintain_for: Yup.string().max(300, "Too long"),
  revoke_for: Yup.string().max(300, "Too long"),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(80, "Too long"),
        years: yearRange,
        account_no: Yup.string().max(20, "Too long"),
        period_begin: Yup.string().max(10, "Too long"),
        period_end: Yup.string().max(10, "Too long"),
      }),
    )
    .max(MAX_PAR101_MATTERS),
});

function toInitialValues(data: Record<string, unknown>): Form_par101_Data {
  const d = data as Partial<Form_par101_Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters: d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    maintain_auth: d.maintain_auth ?? false,
    maintain_for: d.maintain_for ?? "",
    revoke_auth: d.revoke_auth ?? false,
    revoke_for: d.revoke_for ?? "",
    signer: d.signer ?? "",
    signer_title: d.signer_title ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<Par101GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as Par101GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** The VA reps table: which practitioners are active, their Part II designation
 *  letter, VA enrolled-agent number, and the two per-rep mail flags. */
function Par101RepresentativesTable() {
  const { values } = useFormikContext<Form_par101_Data>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-48">Designation</TableHead>
            <TableHead className="w-40">VA Agent No.</TableHead>
            <TableHead className="w-28">No mail</TableHead>
            <TableHead className="w-28">Email copies</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.agent_no`}
                  placeholder="A-000000"
                  aria-label={`VA agent number for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.dont_mail`}
                  aria-label={`Do not mail correspondence to ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.mail_email`}
                  aria-label={`Mail email copies to ${rep.name}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** The six-row Virginia matters grid: Tax Type + Taxable Years for every row,
 *  plus VA account number and beginning/ending period. */
function Par101MattersTable() {
  const { values } = useFormikContext<Form_par101_Data>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax Type</TableHead>
                  <TableHead className="w-40">Taxable Years</TableHead>
                  <TableHead className="w-44">VA Account No.</TableHead>
                  <TableHead className="w-32">Begin (MM/YYYY)</TableHead>
                  <TableHead className="w-32">End (MM/YYYY)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Individual Income"
                        aria-label={`Tax type for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Taxable years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.account_no`}
                        placeholder="Optional"
                        aria-label={`VA account number for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.period_begin`}
                        placeholder="MM/YYYY"
                        aria-label={`Begin period for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.period_end`}
                        placeholder="MM/YYYY"
                        aria-label={`End period for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_PAR101_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_PAR101_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form PAR 101 fits up to {MAX_PAR101_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

function AuthTextField({ name, label }: { name: string; label: string }) {
  const [field] = useField(name);
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Textarea id={name} rows={2} {...field} />
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Virginia Form PAR 101 (Power of Attorney and Declaration of
 * Representative) — prefilled from the client + its practitioners, with the
 * shared Save / Preview / Save-to-Documents action bar. State-specific: per-rep
 * VA enrolled-agent number + mail flags, a six-row matter grid, and the
 * maintain/revoke prior-authorization fields.
 */
export function FormPar101Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <Par101RepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <Par101MattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Prior Authorizations</SectionHeading>
          <FormikCheckbox
            name="maintain_auth"
            label="Maintain (do not revoke) prior powers of attorney, except as noted"
          />
          <AuthTextField name="maintain_for" label="Exceptions to maintain" />
          <FormikCheckbox
            name="revoke_auth"
            label="Revoke a prior power of attorney"
          />
          <AuthTextField
            name="revoke_for"
            label="Agent's name, address, ZIP code and date granted (to revoke)"
          />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
